/**
 * Inspector Client Entry Point
 *
 * Bootstrap sequence:
 *   1. Validate required environment variables (fail-fast)
 *   2. Initialize i18n (async locale loading via Suspense)
 *   3. Mount React tree: StrictMode > AuthProvider > ApolloProvider > Router > App
 *
 * In mock mode (VITE_MOCK_MODE=true):
 *   - Apollo client is replaced with mock client returning pre-canned data
 *   - Auth always uses real Keycloak — mock mode only bypasses the backend
 */

/**
 * Inspector Client Entry Point
 *
 * Bootstrap sequence:
 *   1. await loadConfig()        Fetches /config.json (runtime keys, highest priority)
 *   2. validateRequiredEnv()     Fail-fast check before mount
 *   3. Initialize i18n (async locale loading via Suspense)
 *   4. Mount React tree: StrictMode > AuthProvider > ApolloProvider > Router > App
 *
 * In mock mode (VITE_MOCK_MODE=true):
 *   - Apollo client is replaced with mock client returning pre-canned data
 *   - Auth always uses real Keycloak — mock mode only bypasses the backend
 */

import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { ApolloProvider } from '@apollo/client/react';
import { BrowserRouter } from 'react-router-dom';
import { loadConfig } from '@rtvloco/platform/env';
import { validateRequiredEnv } from './config/envCheck';
import { initAppTracing } from './observability/tracing';
import { themeManager } from '@rtvloco/theme/ThemeManager';
import { AuthProvider } from '@rtvloco/auth/react';
import { PreferencesProvider } from '@rtvloco/user-preferences';
// NOTE: Do NOT statically import `./preferences/setup`, `./auth/config`, or
// `./graphql/client/inspectorApolloClient` here. They construct the
// OidcAuthManager / Apollo client at module-init time. Those modules MUST be
// imported AFTER `await loadConfig()` so resolveOidcAuthority() reads the
// runtime keycloakUrl from /config.json instead of falling back to the
// build-time localhost default.
import '@rtvloco/theme/base.css';
import '@rtvloco/theme/light.css';
import '@rtvloco/theme/dark.css';
import '@rtvloco/ui/shell/styles';
import './styles/app-tokens.css';
import './i18n';
import './styles/index.css';
import './App.css';


// Apply persisted theme before first render via ThemeManager (reads 'smarttt-theme').
themeManager.initialize();

// ---------------------------------------------------------------------------
// Loading skeleton shown while i18n locale files load via Suspense
// ---------------------------------------------------------------------------

const AppLoadingSkeleton = () => (
  <div className="app-loading-skeleton">
    <div className="app-loading-spinner" aria-label="Loading application..." />
    <p className="app-loading-text">Loading SMARTTT Inspector...</p>
  </div>
);

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

async function bootstrap() {
  await loadConfig();
  initAppTracing();
  // Auth manager, Apollo client, and preferences setup imported AFTER
  // loadConfig() so their module-level code runs with _config already
  // populated. Static imports of these modules from this file would
  // trigger OidcAuthManager construction with the build-time localhost
  // fallback before /config.json is read.
  const [
    { inspectorAuthManager },
    { inspectorApolloClient },
    { preferencesConfig },
    { default: App },
  ] = await Promise.all([
    import('./auth/config'),
    import('./graphql/client/inspectorApolloClient'),
    import('./preferences/setup'),
    import('./App'),
  ]);

  validateRequiredEnv();

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error(
      '[Inspector] Root element #root not found. Ensure index.html contains <div id="root"></div>.',
    );
  }

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <Suspense fallback={<AppLoadingSkeleton />}>
        <AuthProvider manager={inspectorAuthManager}>
          <ApolloProvider client={inspectorApolloClient}>
            <PreferencesProvider config={preferencesConfig}>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </PreferencesProvider>
          </ApolloProvider>
        </AuthProvider>
      </Suspense>
    </React.StrictMode>,
  );
}

bootstrap().catch((err) => {
  const root = document.getElementById('root');
  if (root) root.textContent = String(err);
});
